import React, { useCallback, useRef, useState } from 'react';
import { toast } from '../core';

interface SwipeableToastWrapperProps {
  children: React.ReactNode;
  toastId: string | number;
  position?: string;
  closeToast: (shouldSkipAnimation?: boolean) => void;
  collapseAll: () => void;
}

const SWIPE_THRESHOLD = 80; // pixels to swipe before dismissing
const SWIPE_VELOCITY_THRESHOLD = 0.5; // pixels per ms

export function SwipeableToastWrapper({
  children,
  toastId,
  position,
  closeToast,
  collapseAll
}: SwipeableToastWrapperProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(1);

  const dragStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const dragCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const isTopPosition = position?.startsWith('top');

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    setIsDragging(true);
    dragStartRef.current = { x: clientX, y: clientY, time: Date.now() };
    dragCurrentRef.current = { x: clientX, y: clientY };
  }, []);

  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStartRef.current) return;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;

      dragCurrentRef.current = { x: clientX, y: clientY };

      // Only allow upward swipe for top position, downward for bottom
      const shouldTranslateY = isTopPosition ? deltaY < 0 : deltaY > 0;

      setTranslate({
        x: deltaX,
        y: shouldTranslateY ? deltaY : 0
      });

      // Calculate opacity based on distance
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const newOpacity = Math.max(0.3, 1 - distance / (SWIPE_THRESHOLD * 2));
      setOpacity(newOpacity);
    },
    [isTopPosition]
  );

  const handleDragEnd = useCallback(() => {
    if (!dragStartRef.current || !dragCurrentRef.current) {
      setIsDragging(false);
      return;
    }

    const deltaX = dragCurrentRef.current.x - dragStartRef.current.x;
    const deltaY = dragCurrentRef.current.y - dragStartRef.current.y;
    const deltaTime = Date.now() - dragStartRef.current.time;

    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Check if horizontal swipe threshold is met
    const horizontalSwipe = Math.abs(deltaX) > SWIPE_THRESHOLD || velocityX > SWIPE_VELOCITY_THRESHOLD;

    // Check if vertical swipe threshold is met (only upward for top, downward for bottom)
    const verticalSwipe = isTopPosition
      ? deltaY < -SWIPE_THRESHOLD || (deltaY < 0 && velocityY > SWIPE_VELOCITY_THRESHOLD)
      : deltaY > SWIPE_THRESHOLD || (deltaY > 0 && velocityY > SWIPE_VELOCITY_THRESHOLD);

    if (horizontalSwipe || verticalSwipe) {
      // Animate out and dismiss
      const exitX = horizontalSwipe ? (deltaX > 0 ? 400 : -400) : translate.x;
      const exitY = verticalSwipe ? (isTopPosition ? -200 : 200) : 0;

      setTranslate({ x: exitX, y: exitY });
      setOpacity(0);

      setTimeout(() => {
        closeToast(true);
        collapseAll();
      }, 150);
    } else {
      // Snap back
      setTranslate({ x: 0, y: 0 });
      setOpacity(1);
    }

    setIsDragging(false);
    dragStartRef.current = null;
    dragCurrentRef.current = null;
  }, [toastId, translate.x, isTopPosition, closeToast, collapseAll]);

  // Mouse events
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      handleDragMove(e.clientX, e.clientY);
    },
    [isDragging, handleDragMove]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    handleDragEnd();
  }, [isDragging, handleDragEnd]);

  // Touch events
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        handleDragStart(touch.clientX, touch.clientY);
      }
    },
    [handleDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      if (touch) {
        handleDragMove(touch.clientX, touch.clientY);
      }
    },
    [isDragging, handleDragMove]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    handleDragEnd();
  }, [isDragging, handleDragEnd]);

  return (
    <div
      ref={elementRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{
        transform: `translate(${translate.x}px, ${translate.y}px)`,
        opacity,
        transition: isDragging ? 'none' : 'transform 0.15s ease-out, opacity 0.15s ease-out',
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none'
      }}
    >
      {children}
    </div>
  );
}
